// ── Replay Player ──
// Playback engine for recorded replays with timeline control.
// Supports variable speed, seek by tick or round, pause/resume.

import { type ReplayData, type ReplayFrame } from './replay-recorder';

export interface RoundMarker {
  round: number;
  tick: number;
}

const MIN_SPEED = 0.25;
const MAX_SPEED = 4.0;

export class ReplayPlayer {
  private replay: ReplayData | null = null;
  private currentTick = 0;
  private playbackSpeed = 1.0;
  private playing = false;
  private paused = false;
  private accumulatedTime = 0; // seconds of replay time elapsed

  /** Load replay data and reset playback state. */
  load(data: ReplayData): void {
    this.replay = data;
    this.currentTick = 0;
    this.accumulatedTime = 0;
    this.playbackSpeed = 1.0;
    this.playing = false;
    this.paused = false;
  }

  /** Get the loaded replay data. */
  getReplay(): ReplayData | null {
    return this.replay;
  }

  /** Start playback from the beginning. */
  play(): void {
    if (!this.replay) return;
    this.playing = true;
    this.paused = false;
    this.currentTick = 0;
    this.accumulatedTime = 0;
  }

  /** Pause playback (preserving position). */
  pause(): void {
    if (this.playing) {
      this.paused = true;
    }
  }

  /** Resume from paused state. */
  resume(): void {
    if (this.playing && this.paused) {
      this.paused = false;
    }
  }

  /** Stop playback and reset to beginning. */
  stop(): void {
    this.playing = false;
    this.paused = false;
    this.currentTick = 0;
    this.accumulatedTime = 0;
  }

  /** Whether playback is active (may be paused). */
  isPlaying(): boolean {
    return this.playing;
  }

  /** Whether playback is paused. */
  isPaused(): boolean {
    return this.paused;
  }

  /**
   * Advance playback by the given real-time delta (in seconds).
   * Returns the current frame to render, or null if not playing.
   */
  update(dt: number): ReplayFrame | null {
    if (!this.replay || !this.playing || this.paused) return null;

    const tickRate = this.replay.tickRate;
    const maxTick = this.replay.frames.length - 1;

    // Advance accumulated time
    this.accumulatedTime += dt * this.playbackSpeed;

    // Convert time to tick
    const targetTick = Math.floor(this.accumulatedTime * tickRate);
    this.currentTick = Math.min(targetTick, maxTick);

    // Check if we've reached the end
    if (this.currentTick >= maxTick) {
      this.currentTick = maxTick;
      this.playing = false;
      this.paused = false;
      return this.replay.frames[maxTick];
    }

    return this.replay.frames[this.currentTick];
  }

  /** Get the current frame without advancing. */
  getCurrentFrame(): ReplayFrame {
    if (!this.replay || this.replay.frames.length === 0) {
      throw new Error('No replay loaded');
    }
    const clampedTick = Math.max(0, Math.min(this.currentTick, this.replay.frames.length - 1));
    return this.replay.frames[clampedTick];
  }

  /** Seek to a specific tick number. */
  seekToTick(tick: number): void {
    if (!this.replay) return;
    const maxTick = this.replay.frames.length - 1;
    this.currentTick = Math.max(0, Math.min(tick, maxTick));
    this.accumulatedTime = this.currentTick / this.replay.tickRate;
  }

  /** Seek to the first tick of a given round number. */
  seekToRound(round: number): void {
    if (!this.replay) return;

    for (let i = 0; i < this.replay.frames.length; i++) {
      if (this.replay.frames[i].roundNumber === round) {
        this.seekToTick(i);
        return;
      }
    }
  }

  /** Set playback speed (clamped to 0.25x - 4.0x). */
  setSpeed(speed: number): void {
    this.playbackSpeed = Math.max(MIN_SPEED, Math.min(MAX_SPEED, speed));
  }

  /** Get current playback speed. */
  getSpeed(): number {
    return this.playbackSpeed;
  }

  /** Total replay duration in seconds. */
  getTotalDuration(): number {
    if (!this.replay) return 0;
    return this.replay.duration;
  }

  /** Current playback time in seconds. */
  getCurrentTime(): number {
    if (!this.replay) return 0;
    return this.currentTick / this.replay.tickRate;
  }

  /** Progress from 0 (start) to 1 (end). */
  getProgress(): number {
    if (!this.replay || this.replay.frames.length <= 1) return 0;
    return this.currentTick / (this.replay.frames.length - 1);
  }

  /** Get round markers — tick positions where each round starts. */
  getRoundMarkers(): RoundMarker[] {
    if (!this.replay) return [];

    const markers: RoundMarker[] = [];
    let lastRound = -1;

    for (let i = 0; i < this.replay.frames.length; i++) {
      const round = this.replay.frames[i].roundNumber;
      if (round !== lastRound) {
        markers.push({ round, tick: i });
        lastRound = round;
      }
    }

    return markers;
  }
}
